///
/// EarthBrowser
///
/// Created by Matt Giger on 4/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Lightbulb } from "lucide-react"
import type { OnboardingStep as StepData } from "./onboardingSteps"

interface OnboardingStepProps {
	step: StepData
	isBeta: boolean
}

export function OnboardingStep({ step, isBeta }: OnboardingStepProps) {
	const Icon = step.icon

	return (
		<div className="flex flex-col items-center text-center px-2">
			{/* Icon with gradient glow */}
			<div className="relative mb-6">
				<div className={`absolute inset-0 bg-gradient-to-br ${step.color} rounded-2xl blur-xl opacity-60`} />
				<div className="relative bg-background/50 p-5 rounded-2xl border border-slate-700">
					<Icon className="h-12 w-12 text-foreground" />
				</div>
				{step.badge && isBeta && (
					<div className="absolute -top-2 -right-3 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-[10px] font-bold uppercase tracking-wider">
						{step.badge}
					</div>
				)}
			</div>

			{/* Title */}
			<h2 className={`text-2xl font-bold mb-3 bg-gradient-to-r ${step.color} bg-clip-text text-transparent`}>
				{step.title}
			</h2>

			{/* Description */}
			<p className="text-muted-foreground text-base mb-6 max-w-md">
				{step.description}
			</p>

			{/* Custom content */}
			{step.content && <step.content />}

			{/* Tips */}
			{step.tips.length > 0 && (
				<div className="w-full max-w-md rounded-xl border border-slate-700 bg-background/50 backdrop-blur-sm p-4">
					<div className="flex items-center gap-2 mb-3">
						<Lightbulb className="h-4 w-4 text-amber-500" />
						<span className="text-sm font-semibold text-foreground">Tips</span>
					</div>
					<ul className="space-y-2">
						{step.tips.map((tip, i) => (
							<li key={i} className="flex items-start gap-2.5 text-left">
								<div className={`mt-1.5 h-1.5 w-1.5 rounded-full bg-gradient-to-br ${step.color} flex-shrink-0`} />
								<span className="text-sm text-muted-foreground">{tip}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	)
}
